import React, { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import NotificationRow from "./NotificationRow";
import Loader from "../Live/Loader";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import { showToast } from "../../utils/utils";
import {
  deleteAllNotifications,
  deleteNotification,
  fetchNotifications,
  markAllAsRead,
} from "../../services/notificationService";
import {
  getUnClickedNotification,
  updateUnClickedNumberNotification,
} from "../../features/notifications/notificationSlice";

const dropdownVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 },
};

const NotificationComponent = ({ openBell, setOpenBell }) => {
  const [notifications, setNotifications] = useState([]);
  const [loader, setLoader] = useState(true);
  const user = useSelector(selectUserInformations);
  const unClickedNotifications = useSelector(getUnClickedNotification);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * Load notifications from the backend
   */
  const loadNotifications = useCallback(async () => {
    if (!user?.token) {
      console.warn('No user token available');
      return;
    }

    setLoader(true);
    try {
      const data = await fetchNotifications(user.token);
      if (data.status === 200 && data.errorCode === 0) {
        setNotifications(data.data.items || []);
      } else {
        console.error("Error loading notifications:", data.message);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      showToast("Error", "Failed to fetch notifications", "error");
    } finally {
      setLoader(false);
    }
  }, [user?.token]);

  /**
   * Handle deleting a single notification
   */
  const handleDeleteNotification = async (notificationId) => {
    if (!user?.token) return;

    setLoader(true);
    try {
      const data = await deleteNotification(notificationId, user.token);
      if (data.status === 200 || data.errorCode === 0) {
        showToast("Success", data.message, "success");
        
        // Update unclicked count if necessary
        const deletedNotification = notifications.find(n => n.id === notificationId);
        if (deletedNotification && !deletedNotification.pivot?.isClicked) {
          dispatch(updateUnClickedNumberNotification(Math.max(0, unClickedNotifications - 1)));
        }
        
        // Reload notifications
        await loadNotifications();
      }
    } catch (error) {
      console.error("Failed to delete notification:", error);
      showToast("Error", "Failed to delete notification", "error");
    } finally {
      setLoader(false);
    }
  };

  /**
   * Mark all notifications as read
   */
  const handleMarkAllAsRead = async () => {
    if (!user?.token) return;

    try {
      const data = await markAllAsRead(user.token);
      if (data.status === 200 || data.errorCode === 0) {
        showToast("Success", "All notifications marked as read", "success");
        dispatch(updateUnClickedNumberNotification(0));
        await loadNotifications();
      }
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      showToast("Error", "Failed to mark all as read", "error");
    }
  };

  /**
   * Delete all notifications
   */
  const handleDeleteAll = async () => {
    if (!user?.token) return;

    setLoader(true);
    try {
      const data = await deleteAllNotifications(user.token);
      if (data.status === 200 || data.errorCode === 0) {
        showToast("Success", data.message, "success");
        setOpenBell(false);
        dispatch(updateUnClickedNumberNotification(0));
        setNotifications([]);
      }
    } catch (error) {
      console.error("Failed to delete all notifications:", error);
      showToast("Error", "Failed to delete all notifications", "error");
    } finally {
      setLoader(false);
    }
  };

  // Load notifications on mount
  useEffect(() => {
    if (openBell) {
      loadNotifications();
    }
  }, [openBell, loadNotifications]);

  return (
    <AnimatePresence>
      {openBell && (
        <motion.div
          variants={dropdownVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="relative right-0"
        >
          {/* Triangle indicator */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-4 md:top-6 right-1/3 translate-x-1/3 w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[12px] border-b-white z-50"
          />

          {/* Notification container */}
          <motion.div
            className="absolute top-8 md:top-8 right-0 md:right-0 bg-white rounded-xl shadow-lg z-50 w-screen max-w-[95vw] md:w-[580px] h-[70vh] md:h-[60vh] overflow-hidden"
            transition={{ type: "spring", stiffness: 200 }}
          >
            {/* Notifications list */}
            <div className="h-[calc(100%-44px)] overflow-y-auto p-4">
              {loader ? (
                <div className="flex justify-center items-center h-full">
                  <Loader />
                </div>
              ) : notifications.length > 0 ? (
                <div className="space-y-2">
                  {notifications.map((notification, index) => (
                    <NotificationRow
                      key={notification.id || index}
                      notification={notification}
                      user={user}
                      navigate={navigate}
                      onDelete={handleDeleteNotification}
                      onReload={loadNotifications}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex justify-center items-center h-full text-gray-500 text-center p-4">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🔔</div>
                    <p className="text-lg font-medium">No notifications yet</p>
                    <p className="text-sm">We'll keep you posted!</p>
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <motion.div
              className="flex flex-col md:flex-row justify-between items-center bg-primary px-4 py-3 rounded-b-xl border-t border-gray-100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <button
                onClick={handleMarkAllAsRead}
                disabled={notifications.length === 0}
                className="text-white text-sm md:text-base mb-2 md:mb-0 hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                Mark all as read
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={notifications.length === 0}
                className="text-white text-sm md:text-base hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                Delete all
              </button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationComponent;
